<template>
  <Dialog
    :visible="visible"
    @update:visible="(v: boolean) => emit('update:visible', v)"
    :modal="true"
    header="Download CBT Report"
    :style="{ width: '600px', maxWidth: '95vw' }"
    :pt="{ header: { class: 'border-b border-gray-100 pb-4' } }"
  >
    <div class="space-y-4">
      <p class="text-sm text-gray-600">
        Generate a PDF of <span class="font-medium">{{ studentLabel }}</span>'s CBT results.
        Leave all tests selected for a full history, or pick specific tests.
      </p>

      <div v-if="loadingSessions" class="flex justify-center p-8">
        <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
      </div>

      <div v-else-if="completedSessions.length === 0" class="text-center p-8 bg-gray-50 rounded-lg text-gray-500">
        <i class="pi pi-info-circle text-3xl mb-2 text-gray-400"></i>
        <p>This student hasn't completed any CBT sessions yet.</p>
      </div>

      <div v-else class="space-y-2">
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-700 font-medium">{{ selectedCount }} of {{ uniqueTests.length }} tests selected</span>
          <div class="flex gap-2">
            <Button label="Select all" link size="small" @click="selectAll" />
            <Button label="Clear" link size="small" @click="clearAll" />
          </div>
        </div>

        <div class="border border-gray-200 rounded-lg max-h-[40vh] overflow-y-auto">
          <div
            v-for="test in uniqueTests"
            :key="test.id"
            class="flex items-center gap-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 cursor-pointer"
            @click="toggle(test.id)"
          >
            <Checkbox :inputId="`cbt-${test.id}`" :modelValue="selectedTestIds.includes(test.id)"
              :binary="true" @click.stop="toggle(test.id)" />
            <label :for="`cbt-${test.id}`" class="flex-1 cursor-pointer">
              <div class="text-sm font-medium text-gray-900">{{ test.title }}</div>
              <div class="text-xs text-gray-500 mt-0.5">
                <span v-if="test.subjectName">{{ test.subjectName }} &middot; </span>
                {{ test.sessionCount }} {{ test.sessionCount === 1 ? 'attempt' : 'attempts' }}
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2 px-2 pb-2">
        <Button size="small" label="Cancel" text severity="secondary" @click="emit('update:visible', false)" />
        <Button size="small"
          label="Generate PDF"
          icon="pi pi-download"
          :loading="generating"
          :disabled="completedSessions.length === 0"
          @click="generate"
          data-testid="btn-student-cbt-report-generate"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useListStudentCbtTestSessionsQuery, useDownloadStudentCbtReportLazyQuery } from '@/types/vue-apollo-helpers';
import { useToast } from 'primevue/usetoast';
import { loadOrRefetch } from '@/utils/lazyQuery';
import { getErrorMessage } from '@/utils/error';

const props = defineProps<{
  visible: boolean;
  studentId: string;
  studentLabel: string;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
}>();

const toast = useToast();

// Pull every CBT session for this student so we can offer them as filters.
// The query is enabled only when the dialog is open to avoid wasted fetches.
const enabled = computed(() => props.visible && !!props.studentId);
const { result: sessionsResult, loading: loadingSessions } = useListStudentCbtTestSessionsQuery(
  () => ({ studentId: props.studentId }),
  () => ({ enabled: enabled.value, fetchPolicy: 'cache-and-network' }),
);

const completedSessions = computed(() =>
  (sessionsResult.value?.listStudentCBTTestSessions?.data ?? []).filter(
    (s) => s?.status === 'completed',
  ),
);

// Roll up sessions per test — the user selects tests, not individual attempts.
const uniqueTests = computed<{ id: string; title: string; subjectName: string | null; sessionCount: number }[]>(() => {
  const byId = new Map<string, { id: string; title: string; subjectName: string | null; sessionCount: number }>();
  for (const session of completedSessions.value) {
    const test = session?.cbtTest;
    if (!test?.id) continue;
    const existing = byId.get(test.id);
    if (existing) {
      existing.sessionCount++;
    } else {
      byId.set(test.id, {
        id: test.id,
        title: test.title || '(Untitled)',
        subjectName: test.subject?.name ?? null,
        sessionCount: 1,
      });
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.title.localeCompare(b.title));
});

const selectedTestIds = ref<string[]>([]);
const selectedCount = computed(() => selectedTestIds.value.length);

// Default to "everything selected" whenever the available tests change while
// the dialog is open. Reset to empty when the dialog closes.
watch([uniqueTests, () => props.visible], ([tests, isVisible]) => {
  if (isVisible) {
    selectedTestIds.value = tests.map((t) => t.id);
  } else {
    selectedTestIds.value = [];
  }
}, { immediate: true });

function selectAll(): void {
  selectedTestIds.value = uniqueTests.value.map((t) => t.id);
}
function clearAll(): void {
  selectedTestIds.value = [];
}
function toggle(id: string): void {
  const idx = selectedTestIds.value.indexOf(id);
  if (idx >= 0) selectedTestIds.value.splice(idx, 1);
  else selectedTestIds.value.push(id);
}

// Every Generate must queue a new report, so never answer from the cache.
const reportQuery = useDownloadStudentCbtReportLazyQuery(undefined, { fetchPolicy: 'network-only' });
const generating = reportQuery.loading;

async function generate(): Promise<void> {
  // Selecting every test is equivalent to "no filter" — sending an empty array
  // lets the backend include the full history (cheaper than enumerating).
  const allSelected = selectedTestIds.value.length === uniqueTests.value.length;
  const cbt_test_ids = allSelected ? null : selectedTestIds.value;

  try {
    const result = await loadOrRefetch(reportQuery, { student_id: props.studentId, cbt_test_ids });
    if (result?.downloadStudentCbtReport?.id) {
      toast.add({
        severity: 'success',
        summary: 'Report queued',
        detail: 'Your PDF is being generated. Check the Downloads icon in the top bar for progress.',
        life: 5000,
      });
      emit('update:visible', false);
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Could not generate report',
      detail: getErrorMessage(error, 'Unknown error.'),
      life: 6000,
    });
  }
}
</script>
